import { useClientTranslation } from '@/lib/core/i18n';
import { formatAmount, parseText } from '@/lib/core/utils';

export interface BookingSummaryRow {
  label: string;
  value: string;
  hint?: string;
}

interface BookingSummaryPanelProps {
  rows: BookingSummaryRow[];
  /** Same figure already shown on the "GO TO PAYMENT" bar (`purchase.amountMajor`), major units. */
  totalMajor: number;
  currencyCode?: string;
  locale: string;
}

/**
 * A single label → value price line. Mirrors the private `SummaryRow` in
 * `shop/checkout/OrderSummaryAccordionContent.tsx:135-147`; copied rather than imported because
 * that component's body is bound to `CartProductItemDto[]`, which a booking has no equivalent of.
 */
function PriceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-4">
      <span className="font-pp-neue-montreal text-jncquoi-neutral-700 min-w-0 flex-1 text-[14px] leading-normal font-normal tracking-[0.5px]">
        {label}
      </span>
      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal font-normal tracking-[0.5px] whitespace-nowrap">
        {value}
      </span>
    </div>
  );
}

/**
 * A single label → value row. Mirrors the private `SummaryRow` in
 * `shop/checkout/OrderSummaryAccordionContent.tsx:135-147`; copied rather than imported because
 * that component's body is bound to `CartProductItemDto[]`, which a booking has no equivalent of.
 */
function SummaryRow({ label, value, hint }: BookingSummaryRow) {
  return (
    <div className="flex items-start justify-between gap-6 py-3">
      <span className="font-pp-neue-montreal text-jncquoi-neutral-800 shrink-0 text-[12px] leading-[18px] tracking-[0.5px] uppercase">
        {label}
      </span>
      <span className="flex flex-col items-end text-right">
        <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-[18px] tracking-[0.5px]">
          {parseText(value)}
        </span>
        {hint && (
          <span className="font-pp-neue-montreal text-jncquoi-neutral-600 text-[11px] leading-[16px] tracking-[0.5px]">
            {parseText(hint)}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Contents of the "VIEW BOOKING SUMMARY" accordion (Figma: Checkout | Payment methods - view
 * booking summary, node 7050:68065).
 *
 * Context-agnostic on purpose: each checkout adapter (Event, Mews, Ride) builds its own rows from
 * its own booking shape — an occurrence's Where/When/HowMany for events, a reservation's Where/When
 * for Mews — and this just renders whatever it's handed. Skipping a row is the caller's decision
 * (e.g. an occurrence with no venue or no explicit time), not this component's.
 *
 * The Subtotal/Applied discount/Total block above the rows mirrors the shop's own
 * `OrderSummaryAccordionContent`, but none of Event, Mews or Ride model a discount of their own
 * yet — only `totalMajor` (the same figure already charged) is real. Subtotal defaults to it and
 * discount to 0, rendered as structural rows matching the Figma layout rather than omitted, ready
 * to carry a real value the moment a booking context has one.
 *
 * The design also draws a "WHICH CATEGORY" block listing ticket categories with per-line details.
 * The events contract has no such concept — an occurrence carries a single price, capacity,
 * available spots and a guest limit, and nothing that maps to named categories (contract v1.12
 * §3.4) — so that block is intentionally absent rather than filled with invented data. It needs a
 * contract change before it can be rendered.
 */
export function BookingSummaryPanel({ rows, totalMajor, currencyCode, locale }: BookingSummaryPanelProps) {
  const t = useClientTranslation();

  return (
    <div className="flex flex-col">
      <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b-[0.5px] pb-4">
        <PriceRow label={t('App.Checkout.Subtotal')} value={formatAmount(totalMajor, currencyCode, locale)} />
        <PriceRow label={t('App.Checkout.AppliedDiscount')} value={formatAmount(0, currencyCode, locale)} />
        <div className="flex items-center gap-4">
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-[14px] leading-normal font-semibold tracking-[0.5px]">
            {t('App.Checkout.Total')}
          </span>
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-normal font-semibold tracking-[0.25px] whitespace-nowrap">
            {formatAmount(totalMajor, currencyCode, locale)}
          </span>
        </div>
      </div>
      <div className="divide-jncquoi-neutral-200 flex flex-col divide-y-[0.5px]">
        {rows.map((row, index) => (
          <SummaryRow key={index} {...row} />
        ))}
      </div>
    </div>
  );
}
